/**
 * Chummys occasions — <chummys-occasions>
 *
 * Three small jobs, all in service of the shelf:
 *  - measure where the page-width header starts so the shelf's first card
 *    lines up with it (--occ-edge), and keep that right on resize
 *  - add `is-in` once so the cards arrive, and write --occ-p as the section
 *    crosses the viewport for the shelf's slight drift
 *  - arrows that step one card, and mouse drag-to-scroll that does not fire
 *    the card's link when what you did was drag
 */
(() => {
  if (customElements.get('chummys-occasions')) return;

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ChummysOccasions extends HTMLElement {
    constructor() {
      super();
      this.visible = false;
      this.raf = null;
      this.drag = null;
      this.onScroll = this.onScroll.bind(this);
      this.tick = this.tick.bind(this);
      this.measure = this.measure.bind(this);
      this.updateArrows = this.updateArrows.bind(this);
    }

    connectedCallback() {
      this.shelf = this.querySelector('.chummys-occ__shelf');
      this.head = this.querySelector('.chummys-occ__head');
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      if (!this.shelf) return;

      this.measure();
      window.addEventListener('resize', this.measure, { passive: true });

      this.io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          this.visible = e.isIntersecting;
          if (e.isIntersecting) { this.classList.add('is-in'); this.onScroll(); }
        }
      }, { rootMargin: '10% 0px', threshold: [0, 0.1] });
      this.io.observe(this);

      if (!reduced) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.tick();
      } else {
        this.style.setProperty('--occ-p', '0.5');
      }

      if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
      if (this.next) this.next.addEventListener('click', () => this.step(1));
      this.shelf.addEventListener('scroll', this.updateArrows, { passive: true });
      this.updateArrows();

      this.bindDrag();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.measure);
      if (this.io) this.io.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    /* -------------------------------------------------------------- layout */

    measure() {
      if (!this.head) return;
      const h = this.head.getBoundingClientRect();
      const me = this.getBoundingClientRect();
      const pad = parseFloat(getComputedStyle(this.head).paddingLeft) || 0;
      const edge = Math.max(12, Math.round(h.left - me.left + pad));
      this.style.setProperty('--occ-edge', edge + 'px');
    }

    cardStep() {
      const card = this.shelf.querySelector('.chummys-occ__card');
      if (!card) return 300;
      const gap = parseFloat(getComputedStyle(this.shelf).columnGap) || 16;
      return card.getBoundingClientRect().width + gap;
    }

    step(dir) {
      this.shelf.scrollBy({ left: dir * this.cardStep() * 2, behavior: reduced ? 'auto' : 'smooth' });
    }

    updateArrows() {
      if (!this.prev || !this.next) return;
      const max = this.shelf.scrollWidth - this.shelf.clientWidth - 1;
      this.prev.toggleAttribute('disabled', this.shelf.scrollLeft <= 1);
      this.next.toggleAttribute('disabled', this.shelf.scrollLeft >= max);
    }

    /* ---------------------------------------------------------------- drag */

    bindDrag() {
      const s = this.shelf;
      s.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        this.drag = { x: e.clientX, left: s.scrollLeft, moved: false };
        s.setPointerCapture(e.pointerId);
      });
      s.addEventListener('pointermove', (e) => {
        if (!this.drag) return;
        const dx = e.clientX - this.drag.x;
        if (!this.drag.moved && Math.abs(dx) > 6) {
          this.drag.moved = true;
          this.classList.add('is-dragging');
        }
        if (this.drag.moved) s.scrollLeft = this.drag.left - dx;
      });
      const end = () => {
        if (!this.drag) return;
        const moved = this.drag.moved;
        this.drag = null;
        // let the click that ends a drag die before the cards get their
        // pointer-events back, or a drag would open whatever it ended on
        if (moved) setTimeout(() => this.classList.remove('is-dragging'), 0);
      };
      s.addEventListener('pointerup', end);
      s.addEventListener('pointercancel', end);
    }

    /* -------------------------------------------------------------- scroll */

    onScroll() {
      if (!this.visible || this.raf) return;
      this.raf = requestAnimationFrame(this.tick);
    }

    tick() {
      this.raf = null;
      const r = this.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      this.style.setProperty('--occ-p', clamp((vh - r.top) / (vh + r.height), 0, 1).toFixed(4));
    }
  }

  customElements.define('chummys-occasions', ChummysOccasions);
})();
